import {useEffect, useMemo, useState} from 'react';

import {ParcelasDTO} from '../../../../../dtos/ParcelasDTO';
import {api} from '../../../../../services/api';

interface UseInstallmentsParams {
  enabled: boolean;
  value: number;
  voucher?: boolean | null;
  /** When true, only installmentCount === 1 (e.g. Stripe 3DS à vista) */
  onlyOneInstallment?: boolean;
  refreshKey?: string;
  consulta_id?: string | null;
  uids?: string[] | null;
  uuid?: string | null;
  tipo_operacao?: string;
}

function normalizeInstallment(item: ParcelasDTO): ParcelasDTO {
  return {
    valorMes: item.valorMes,
    installmentCount: item.installmentCount,
    valorCompraTaxa: item.valorCompraTaxa,
    valorTaxa: item.valorTaxa,
    descricao: item.descricao,
    aviso: item.aviso,
    conveniencia: item.conveniencia,
    CET: item.CET,
  };
}

export function useInstallments({
  enabled,
  value,
  voucher,
  onlyOneInstallment,
  refreshKey,
  consulta_id,
  uids,
  uuid,
  tipo_operacao,
}: UseInstallmentsParams) {
  const [installments, setInstallments] = useState<ParcelasDTO[]>([]);
  const [selectedInstallment, setSelectedInstallment] =
    useState<ParcelasDTO | null>(null);
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false);
  const [installmentsError, setInstallmentsError] = useState<string | null>(
    null,
  );

  const availableInstallments = useMemo(
    () =>
      installments.filter(
        item =>
          (!voucher && !onlyOneInstallment) || item.installmentCount === 1,
      ),
    [installments, voucher, onlyOneInstallment],
  );

  useEffect(() => {
    let isMounted = true;

    async function fetchInstallments() {
      if (!enabled) {
        setInstallments([]);
        setSelectedInstallment(null);
        setInstallmentsError(null);
        setIsLoadingInstallments(false);
        return;
      }

      try {
        setIsLoadingInstallments(true);
        setInstallmentsError(null);

        const {data} = await api.get('/totalParcelado', {
          params: {
            value,
            ...(consulta_id && {consulta_id}),
            ...(uids && {uids}),
            ...(uuid && {uuid}),
          },
        });

        let normalizedInstallments = [];
        if (tipo_operacao === 'debito_veicular') {
          normalizedInstallments = data.map((item: ParcelasDTO) =>
            normalizeInstallment(item),
          );
        } else {
          normalizedInstallments = data.map((item: ParcelasDTO) =>
            normalizeInstallment(item),
          ).reverse();
        }

        if (!isMounted) {
          return;
        }

        setInstallments(normalizedInstallments);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setInstallments([]);
        setSelectedInstallment(null);
        setInstallmentsError('Parcelas indisponiveis no momento.');
      } finally {
        if (isMounted) {
          setIsLoadingInstallments(false);
        }
      }
    }

    fetchInstallments();

    return () => {
      isMounted = false;
    };
  }, [enabled, refreshKey, value]);

  useEffect(() => {
    if (!enabled || availableInstallments.length === 0) {
      setSelectedInstallment(null);
      return;
    }

    setSelectedInstallment(availableInstallments[0]);
  }, [availableInstallments, enabled]);

  return {
    installments,
    availableInstallments,
    selectedInstallment,
    setSelectedInstallment,
    isLoadingInstallments,
    installmentsError,
  };
}
